import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { sessionsApi } from '../api/sessions';
import {
  focusColumnsForSessions,
  partitionSessions,
  sessionCardSummary,
  sessionSittingStamp,
} from '../utils/chatSessions';
import { SERIES_DISCOVERY_LABEL } from '../utils/focusTemplates';
import { bookDiscoverySessionId } from '../utils/bookDiscoverySession';
import './ChatSessionsPage.css';

function unwrapSessions(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.sessions)) return res.sessions;
  return [];
}

function SessionCard({ session, variant = 'default', fallbackTitle, onOpen }) {
  const title = (session?.name || fallbackTitle || 'Untitled session').trim();
  return (
    <button
      type="button"
      className={`cs-card cs-card--${variant}`}
      aria-label={title}
      onClick={() => session?.id && onOpen(session.id)}
      disabled={!session?.id}
    >
      <h3 className="cs-card__title">{title}</h3>
      <p className="cs-card__summary">{session ? sessionCardSummary(session) : 'One notebook. Always here.'}</p>
      <span className="cs-card__stamp">{sessionSittingStamp(session?.created_at)}</span>
    </button>
  );
}

export default function ChatSessionsPage() {
  const navigate = useNavigate();
  const { currentStory, currentProject, stories } = useApp();
  const seriesAltitude = Boolean(currentProject?.is_series) && !currentStory?.id;
  const storyId = seriesAltitude ? null : (currentStory?.id || null);
  const projectId = currentProject?.id || null;
  const discoveryId = bookDiscoverySessionId({
    currentStory,
    currentProject,
    stories,
  });
  const discoveryLabel = seriesAltitude
    ? SERIES_DISCOVERY_LABEL
    : (currentStory?.name || currentProject?.name || 'Discovery');

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(Boolean(storyId || projectId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!storyId && !projectId) {
        setSessions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = storyId
          ? await sessionsApi.list(storyId)
          : await sessionsApi.listProject(projectId);
        if (!cancelled) setSessions(unwrapSessions(res));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load sessions.');
          setSessions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [storyId, projectId]);

  const { discovery, explore, focus } = useMemo(
    () => partitionSessions(sessions, discoveryId),
    [sessions, discoveryId],
  );

  const focusColumns = useMemo(
    () => focusColumnsForSessions(focus, { seriesAltitude }),
    [focus, seriesAltitude],
  );

  const discoveryCard = discovery || (discoveryId
    ? { id: discoveryId, name: discoveryLabel }
    : null);

  const openSession = (id) => navigate(`/chat/${id}`);

  const openDiscovery = () => {
    if (discoveryCard?.id) {
      openSession(discoveryCard.id);
      return;
    }
    if (projectId) navigate(`/projects/${projectId}/discovery`);
  };

  if (!storyId && !projectId) {
    return (
      <div className="cs-page">
        <header className="cs-page__intro">
          <h1 className="cs-page__title">Chat Sessions</h1>
          <p className="cs-page__lede">Open a project to see its sessions.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="cs-page">
      <header className="cs-page__intro">
        <h1 className="cs-page__title">Chat Sessions</h1>
        <p className="cs-page__lede">
          Discovery is the notebook you always have. Explore is for trying a
          few directions. Focus is for one thing you keep coming back to.
          Sessions stay.
        </p>
      </header>

      {error && <p className="cs-page__error">{error}</p>}
      {loading && <p className="cs-page__state">Loading sessions…</p>}

      <section className="cs-band" aria-label={seriesAltitude ? 'Series Discovery and Explore' : 'Discovery and Explore'}>
        <div className="cs-band__discovery">
          <p className="cs-kicker">{seriesAltitude ? SERIES_DISCOVERY_LABEL : 'Discovery'}</p>
          <p className="cs-kicker__hint">One notebook. Always here.</p>
          {discoveryCard ? (
            <SessionCard
              session={{ ...discoveryCard, name: seriesAltitude ? SERIES_DISCOVERY_LABEL : discoveryCard.name }}
              variant="discovery"
              fallbackTitle={discoveryLabel}
              onOpen={openDiscovery}
            />
          ) : (
            <button type="button" className="cs-card cs-card--discovery" onClick={openDiscovery}>
              <h3 className="cs-card__title">{discoveryLabel}</h3>
              <p className="cs-card__summary">One notebook. Always here.</p>
            </button>
          )}
        </div>

        <div className="cs-band__explore">
          <p className="cs-kicker">Explore</p>
          <p className="cs-kicker__hint">A few directions. Not a new pile for each one.</p>
          <div className="cs-explore-row" role="list" aria-label="Explore sessions">
            {explore.length === 0 && !loading && (
              <p className="cs-empty">No Explore sessions yet. Create New Session starts one.</p>
            )}
            {explore.map((session) => (
              <div key={session.id} role="listitem">
                <SessionCard session={session} variant="explore" onOpen={openSession} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cs-focus" aria-label="Focus">
        <p className="cs-kicker">Focus</p>
        <p className="cs-kicker__hint">
          Each kind of chat has its own strip. A long list scrolls there,
          not down the page.
        </p>
        <div className="cs-focus__board">
          {focusColumns.map((column) => (
            <div key={column.key} className="cs-focus__col" aria-label={column.label}>
              <h2 className="cs-focus__col-title">{column.label}</h2>
              <div className="cs-focus__col-scroll" role="list" aria-label={`${column.label} sessions`}>
                {column.sessions.length === 0 && (
                  <p className="cs-empty cs-empty--col">No sessions here yet.</p>
                )}
                {column.sessions.map((session) => (
                  <div key={session.id} role="listitem">
                    <SessionCard session={session} variant="focus" onOpen={openSession} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
